import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { IRequestParams, request } from 'helpers/request';
import { uploadFileToDB } from 'helpers/dbFilesHandlers';
import { CONSTANTS, logSagaErrorMessage } from 'utils';
import { getEndpoint, getEndpointWithData, requestSagaFactory } from 'helpers';
import { IPagination } from 'models';
import * as types from './ActionTypes';
import {
	deleteClient,
	getClientData,
	getClientDetails,
	getClientList,
	getClientSurveyData,
	getClientUsers,
	getPartnerLimits,
	postNewClient,
	updateClientData,
	updateClientList,
} from './clients.actions';
import {
	IClientData,
	IClientFormData,
	IClientList,
	IClientPartnerLimits,
	ISurveyData,
} from './models';
import { mapClientPayload } from './helpers';
import { ISearchPayload, IUserList } from '../../models';
import { AppPathList } from '../../../../router/AppPathList';

const clientId = '/:replaceWithClientId';
const partnerId = '/:replaceWithPartnerId';
const userId = '/:replaceWithUserId';

const mailingListPath = '/clients/file/mailing-list/save?clientId=';

const ENDPOINTS = {
	clients: getEndpoint(AppPathList.clients),
	clientData: getEndpointWithData(`${AppPathList.clients}${clientId}`),
	clientList: getEndpoint(`${AppPathList.clients}/all`),
	client_users: getEndpointWithData(
		`${AppPathList.clients}${userId}/read-only-users`,
	),
	deleteClient: getEndpointWithData(`${AppPathList.clients}${clientId}`),
	clientLimits: getEndpointWithData(
		`${AppPathList.clients}/limits${partnerId}`,
	),
};

function* deleteClientSaga({ payload }: { payload: { id: string } }) {
	try {
		yield put(deleteClient.loading());

		const deleteClientParams: IRequestParams = {
			method: 'DELETE',
			url: ENDPOINTS.deleteClient(payload.id),
		};

		yield call(request, deleteClientParams);

		yield put(deleteClient.success());
	} catch (e) {
		logSagaErrorMessage('deleteClientSaga', e);
		yield put(deleteClient.fail(e));
	}
}

const getClientLimitsSaga = ({
	payload,
}: {
	payload: { partnerId: string; clientId: string };
}) => {
	return requestSagaFactory<{}, IClientPartnerLimits>({
		url: payload.clientId
			? `${ENDPOINTS.clientLimits(payload.partnerId)}?clientId=${
					payload.clientId
			  }`
			: `${ENDPOINTS.clientLimits(payload.partnerId)}`,
		logErrorMessage: 'getClientLimits',
		actionApi: getPartnerLimits,
	})({});
};

function* postNewClientSaga({ payload }: { payload: IClientFormData }) {
	try {
		yield put(postNewClient.loading());

		const { mailingListKeyData } = payload;
		const postNewClientData = mapClientPayload(payload);

		const postNewClientParams: IRequestParams = {
			method: 'POST',
			url: ENDPOINTS.clients,
			data: postNewClientData,
		};

		const { data }: AxiosResponse<{ id: number }> = yield call(
			request,
			postNewClientParams,
		);

		if (mailingListKeyData) {
			yield uploadFileToDB(
				mailingListKeyData as File,
				`${mailingListPath}${data.id}`,
			);
		}

		yield put(postNewClient.success(data));
	} catch (e) {
		logSagaErrorMessage('postNewClient', e);
		yield put(postNewClient.fail(e));
	}
}

function* updateClientDataSaga({ payload }: { payload: IClientFormData }) {
	try {
		yield put(updateClientData.loading());

		const { mailingListKeyData, shouldDelete, shouldUpdate, id } = payload;
		const updateClientPayload = mapClientPayload(payload);

		const postNewClientParams: IRequestParams = {
			method: 'PATCH',
			url: ENDPOINTS.clientData(id as string),
			data: updateClientPayload,
		};

		const { data }: AxiosResponse<IClientData> = yield call(
			request,
			postNewClientParams,
		);

		if (!mailingListKeyData && shouldDelete) {
			yield uploadFileToDB(
				undefined,
				`${mailingListPath}${data.id}&onDeleteFlag=true&language=${navigator.language}`,
			);
		}
		if (mailingListKeyData) {
			yield uploadFileToDB(
				mailingListKeyData as File,
				`${mailingListPath}${data.id}&onDeleteFlag=${shouldUpdate}&language=${navigator.language}`,
			);
		}

		yield put(updateClientData.success(data));
	} catch (e) {
		logSagaErrorMessage('updateClientData', e);
		yield put(updateClientData.fail(e));
	}
}

function* getClientDataSaga({ payload }: { payload: string }) {
	try {
		yield put(getClientData.loading());

		const getClientDataParams: IRequestParams = {
			method: 'GET',
			url: ENDPOINTS.clientData(payload),
		};

		const { data }: AxiosResponse<IClientData> = yield call(
			request,
			getClientDataParams,
		);

		yield put(getClientData.success(data));
	} catch (e) {
		logSagaErrorMessage('getClientData', e);
		yield put(getClientData.fail(e));
	}
}

function* getClientDetailsSaga({ payload }: { payload: string }) {
	try {
		yield put(getClientDetails.loading());

		const getClientDetailsParams: IRequestParams = {
			params: {
				clientId: payload,
			},
			url: ENDPOINTS.clientList,
		};

		const { data }: AxiosResponse<IPagination<IClientList>> = yield call(
			request,
			getClientDetailsParams,
		);

		yield put(getClientDetails.success(data.content && data.content[0]));
	} catch (e) {
		logSagaErrorMessage('getClientDetails', e);
		yield put(getClientDetails.fail(e));
	}
}

function* getClientListSaga({ payload }: { payload?: ISearchPayload }) {
	try {
		yield put(getClientList.loading());

		const getClientListParams: IRequestParams = {
			params: {
				page: payload?.page || 0,
				size: payload?.size || 30,
				formalName: payload?.formalName || '',
				clientId: payload?.clientId,
			},
			url: ENDPOINTS.clientList,
		};

		const { data }: AxiosResponse<IPagination<IClientList>> = yield call(
			request,
			getClientListParams,
		);

		yield put(getClientList.success(data));
	} catch (e) {
		logSagaErrorMessage('getCompanyClients', e);
		yield put(getClientList.fail(e));
	}
}

function* updateClientListSaga({ payload }: { payload?: ISearchPayload }) {
	try {
		yield put(updateClientList.loading());

		const getClientListParams: IRequestParams = {
			params: {
				page: payload?.page || 0,
				size: payload?.size || 30,
				formalName: payload?.formalName || '',
				clientId: payload?.clientId,
			},
			url: ENDPOINTS.clientList,
		};

		const { data }: AxiosResponse<IClientList> = yield call(
			request,
			getClientListParams,
		);

		yield put(updateClientList.success(data));
	} catch (e) {
		logSagaErrorMessage('getCompanyClients', e);
		yield put(updateClientList.fail(e));
	}
}

function* getSurveyDataSaga({
	payload,
}: {
	payload: {
		clientId: string;
	};
}) {
	try {
		yield put(getClientSurveyData.loading());

		const getSurveyDataParams: IRequestParams = {
			url: `${CONSTANTS.baseApiPath}${CONSTANTS.moduleAdminPath}/surveys/stat/client/${payload.clientId}`,
		};

		const { data }: AxiosResponse<ISurveyData> = yield call(
			request,
			getSurveyDataParams,
		);

		yield put(getClientSurveyData.success(data));
	} catch (e) {
		logSagaErrorMessage('getSurveyData', e);
		yield put(getClientSurveyData.fail(e));
	}
}

export function* getClientUsersSaga({ payload }: { payload: string }) {
	try {
		yield put(getClientUsers.loading());

		const getClientUsersParams: IRequestParams = {
			url: ENDPOINTS.client_users(payload),
		};

		const { data }: AxiosResponse<IUserList> = yield call(
			request,
			getClientUsersParams,
		);

		yield put(getClientUsers.success(data));
	} catch (e) {
		logSagaErrorMessage('getSurveyData', e);
		yield put(getClientUsers.fail(e));
	}
}

export function* clientsSaga() {
	yield all([
		// @ts-ignore
		takeLatest(types.GET_CLIENT_LIST.DEFAULT, getClientListSaga),
		// @ts-ignore
		takeLatest(types.UPDATE_CLIENT_LIST.DEFAULT, updateClientListSaga),
		// @ts-ignore
		takeEvery(types.POST_NEW_CLIENT.DEFAULT, postNewClientSaga),
		// @ts-ignore
		takeLatest(types.GET_CLIENT_DETAILS.DEFAULT, getClientDetailsSaga),
		// @ts-ignore
		takeLatest(types.GET_CLIENT_DATA.DEFAULT, getClientDataSaga),
		// @ts-ignore
		takeEvery(types.UPDATE_CLIENT_DATA.DEFAULT, updateClientDataSaga),
		// @ts-ignore
		takeEvery(types.GET_CLIENT_USERS.DEFAULT, getClientUsersSaga),
		// @ts-ignore
		takeEvery(types.DELETE_CLIENT.DEFAULT, deleteClientSaga),
		// @ts-ignore
		takeEvery(types.GET_CLIENT_SURVEY_DATA.DEFAULT, getSurveyDataSaga),
		// @ts-ignore
		takeEvery(types.GET_PARTNER_LIMITS.DEFAULT, getClientLimitsSaga),
	]);
}
